import { useState } from "react";
import { UseFormRegister, FieldErrors, FieldValues } from "react-hook-form";
import { Select, Input } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Wallet } from "lucide-react";
import { translateValidation } from "../../utils/translateValidation";

interface Currency {
  value: string;
  label: string;
}

/**
 * Shared by the shipment form and the container order form. Both carry an
 * identically-shaped `pricing` block, so the section is generic over the form
 * type and casts to the shipment shape internally for the field paths.
 */
interface PricingSectionProps<T extends FieldValues = CreateShipmentData> {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
  currencies: Currency[];
  transportMode?: "ROAD" | "SEA" | "AIR" | "CONTAINER";
}

export function PricingSection<T extends FieldValues = CreateShipmentData>({
  register: registerProp,
  errors: errorsProp,
  readonly,
  getErrorMessage,
  currencies,
  transportMode = "ROAD",
}: PricingSectionProps<T>) {
  const register = registerProp as unknown as UseFormRegister<CreateShipmentData>;
  const errors = errorsProp as FieldErrors<CreateShipmentData>;

  const { t } = useTranslation("shipments");
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Mileage and price/km are per-kilometre road figures — meaningful for a
  // truck run (ROAD, and the drayage legs of CONTAINER) but not for SEA / AIR.
  const isRoad = transportMode === "ROAD" || transportMode === "CONTAINER";

  const th =
    "px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider";
  const td = "px-2 py-1.5 align-top";

  // Collect any pricing error to show below the row in a single line.
  const fieldErrors = [
    getErrorMessage(errors.pricing?.totalBuyingPrice),
    getErrorMessage(errors.pricing?.totalSellingPrice),
    ...(isRoad
      ? [
          getErrorMessage(errors.pricing?.pricePerKm),
          getErrorMessage(errors.pricing?.mileage),
        ]
      : []),
    getErrorMessage(errors.pricing?.currency),
  ].filter(Boolean) as string[];

  return (
    <SectionCard
      icon={Wallet}
      title={t("pricing.title")}
      collapsible
      onToggle={() => setIsCollapsed((p) => !p)}
      actions={
        isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )
      }
    >
      {isCollapsed ? null : (
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className={th}>{t("labels.buyingPrice")}</th>
                  <th className={th}>{t("labels.sellingPrice")}</th>
                  {isRoad && <th className={th}>{t("labels.pricePerKm")}</th>}
                  {isRoad && <th className={th}>{t("labels.mileage")}</th>}
                  <th className={th}>{t("labels.currency")}</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <tr>
                  <td className={td}>
                    <Input
                      {...register("pricing.totalBuyingPrice", {
                        valueAsNumber: true,
                      })}
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      readOnly={readonly}
                      size="sm"
                      error={!!errors.pricing?.totalBuyingPrice}
                    />
                  </td>
                  <td className={td}>
                    <Input
                      {...register("pricing.totalSellingPrice", {
                        valueAsNumber: true,
                      })}
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      readOnly={readonly}
                      size="sm"
                      error={!!errors.pricing?.totalSellingPrice}
                    />
                  </td>
                  {isRoad && (
                    <td className={td}>
                      <Input
                        {...register("pricing.pricePerKm", {
                          valueAsNumber: true,
                        })}
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        readOnly={readonly}
                        size="sm"
                        error={!!errors.pricing?.pricePerKm}
                      />
                    </td>
                  )}
                  {isRoad && (
                    <td className={td}>
                      <Input
                        {...register("pricing.mileage", {
                          valueAsNumber: true,
                        })}
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        readOnly={readonly}
                        size="sm"
                        error={!!errors.pricing?.mileage}
                      />
                    </td>
                  )}
                  <td className={td}>
                    <Select
                      {...register("pricing.currency")}
                      options={currencies}
                      disabled={readonly}
                      size="sm"
                      placeholder={t("pricing.selectCurrency")}
                      error={!!errors.pricing?.currency}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {fieldErrors.length > 0 && (
            <div className="px-3 py-1.5 bg-red-50 border-t border-red-100 text-[11px] text-red-600">
              {fieldErrors.map((e) => translateValidation(e)).join(" · ")}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
